/**
 * Thin fetch wrapper that:
 *  - always sends HTTP-only cookies (`credentials: 'include'`)
 *  - sets `Content-Type: application/json` for requests with a body
 *  - throws an `ApiError` for non-2xx responses
 *  - redirects to `/` on 401 (session expired / not logged in)
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /**
   * When true, a 401 response does NOT trigger the global redirect to "/".
   * Used by the session probe (GET /api/auth/me), where a 401 simply means
   * "not logged in" and must be handled by the caller instead of reloading.
   */
  skipAuthRedirect?: boolean;
};

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers: extraHeaders, skipAuthRedirect, ...rest } = options;

  const headers: HeadersInit = {
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...extraHeaders,
  };

  const response = await fetch(path, {
    ...rest,
    credentials: "include",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    if (skipAuthRedirect) {
      // Caller handles 401 itself (e.g. the session probe) — surface it as an
      // ApiError instead of redirecting.
      throw new ApiError(401, "Unauthenticated");
    }
    // Session expired or not authenticated — send user back to landing/login.
    window.location.replace("/");
    // Return a never-resolving promise so callers don't continue processing.
    return new Promise(() => {});
  }

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = await response.text();
    }
    const message =
      typeof errorBody === "object" &&
      errorBody !== null &&
      "message" in errorBody
        ? String((errorBody as { message: unknown }).message)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, errorBody);
  }

  // 204 No Content — return undefined cast to T
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get<T>(path: string, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "GET" });
  },

  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "POST", body });
  },

  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "PUT", body });
  },

  patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "PATCH", body });
  },

  delete<T>(path: string, options?: Omit<RequestOptions, "method">) {
    return request<T>(path, { ...options, method: "DELETE" });
  },
};
